import React from "react";
import { Check, Calendar } from "lucide-react";
import { GRADE_OPTIONS, AI_LAB_COURSES } from "../../../constants/ai-lab-constants";

const ChildDetailsCard = ({
  childIdx,
  child,
  onFieldChange,
  onCourseClick,
  isChildUser = false,
}) => {
  const formatScheduleBadge = (sched) => {
    if (!sched || !sched.day || !sched.timeSlot) return "";
    const shortDay = sched.day.substring(0, 3);
    const cleanTime = sched.timeSlot
      .replace(/^0/, "")
      .replace(" – 0", " – ")
      .replace(" – ", " - ");
    return `${shortDay} · ${cleanTime}`;
  };

  return (
    <div className="p-5 sm:p-6 border border-gray-200 rounded-3xl bg-white space-y-5 shadow-2xs text-left">
      {/* Card Header */}
      {!isChildUser && (
        <div className="font-black text-sm text-gray-800 border-b border-gray-100 pb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-black">
            {childIdx + 1}
          </span>
          {`Child ${childIdx + 1} details`}
        </div>
      )}

      {/* Fields: Name + Grade side-by-side */}
      {!isChildUser && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter full name"
                value={child.name || ""}
                onChange={(e) => onFieldChange("name", e.target.value)}
                className="w-full text-xs sm:text-sm border border-gray-300 rounded-xl px-3.5 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">
                Class/Grade <span className="text-red-500">*</span>
              </label>
              <select
                value={child.grade || ""}
                onChange={(e) => onFieldChange("grade", e.target.value)}
                className="w-full text-xs sm:text-sm border border-gray-300 rounded-xl px-3.5 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition bg-white"
              >
                <option value="">Select Class</option>
                {GRADE_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Field: School Name below */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">
              School Name (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter school name"
              value={child.school || ""}
              onChange={(e) => onFieldChange("school", e.target.value)}
              className="w-full text-xs sm:text-sm border border-gray-300 rounded-xl px-3.5 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition"
            />
          </div>
        </>
      )}

      {/* Course Selection Area */}
      <div className="space-y-2.5 pt-2">
        <div>
          <label className="block text-xs font-black text-gray-400 uppercase tracking-wider">
            Select Courses & Schedule
          </label>
          <p className="text-[10px] text-gray-500 font-semibold mt-0.5">
            Click on a course to select it and choose its day & time slot.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {AI_LAB_COURSES.map((course) => {
            const isSelected = child.interests?.includes(course.id);
            const sched = child.schedule?.[course.id];

            return (
              <button
                key={course.id}
                type="button"
                title={course.label}
                onClick={() => onCourseClick(course.id)}
                style={
                  isSelected
                    ? {
                        backgroundColor: course.bg,
                        borderColor: course.border,
                        color: course.text,
                      }
                    : {}
                }
                className={`group relative rounded-xl border p-2 text-left flex items-center gap-2 min-h-[52px] transition-all duration-200 select-none ${
                  isSelected
                    ? "ring-2 ring-blue-500/80 scale-[0.98] shadow-xs"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-2xs active:scale-[0.99]"
                }`}
              >
                {/* Checkbox on the left */}
                <div
                  className={`w-3.5 h-3.5 rounded-md border flex items-center justify-center transition-all flex-shrink-0 ${
                    isSelected
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-gray-300 bg-gray-50"
                  }`}
                >
                  {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                </div>

                {/* Course Icon */}
                <span className="text-base filter drop-shadow-xs group-hover:scale-110 transition-all duration-150 flex-shrink-0">
                  {course.icon}
                </span>

                {/* Course Name & Schedule Badge on the right */}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <span
                    className={`font-black text-[9px] sm:text-[10px] block leading-tight truncate ${
                      isSelected ? "text-gray-900" : "text-gray-700"
                    }`}
                  >
                    {course.label}
                  </span>
                  {isSelected && sched && sched.day && sched.timeSlot ? (
                    <span className="inline-flex items-center gap-0.5 text-[8px] font-black uppercase tracking-tight text-blue-700 mt-0.5 max-w-full truncate">
                      {formatScheduleBadge(sched)}
                    </span>
                  ) : isSelected ? (
                    <span className="inline-flex items-center gap-0.5 text-[8px] font-black uppercase tracking-tight text-amber-600 mt-0.5">
                      ⚠️ Set Slot
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChildDetailsCard;
